import type { UseQueryResult } from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { IUserInfo } from "./use-user-info";
import type { NotificationStatus } from "@/constants";
import { QueryKeys } from "@/constants";
import {
  notifications,
  recommendations,
  updateNotificationStatus,
  updateRecommendationStatus,
} from "@/services/notification";
import type { Notification, Recommendation } from "@/model/notification";
import type { AxiosError } from "axios";

import { ErrorNotificationType } from "@/pages/components/error-notification";
import { emitEvent, EventConstants } from "@/lib/event-emitter";

const useNotification = (
  userInfo: IUserInfo,
): UseQueryResult<Notification[], Error> => {
  return useQuery<Notification[], Error>({
    queryKey: [QueryKeys.NOTIFICATIONS, userInfo.username],
    queryFn: () => notifications(userInfo),
    enabled: Boolean(userInfo?.username) && Boolean(userInfo?.accessToken),
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 11, // optional: keeps in memory 1 min after going unused
    retry: false,
  });
};

const useRecommendation = (
  userInfo: IUserInfo,
): UseQueryResult<Recommendation[], Error> => {
  return useQuery<Recommendation[], Error>({
    queryKey: [QueryKeys.RECOMMENDATIONS, userInfo.username],
    queryFn: () => recommendations(userInfo),
    enabled: Boolean(userInfo?.username) && Boolean(userInfo?.accessToken),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
};

const useNotificationStatus = () => {
  return useMutation({
    mutationKey: [QueryKeys.READ_NOTIFICATION],
    mutationFn: ({
      userInfo,
      notification,
      status,
    }: {
      userInfo: IUserInfo;
      notification: Notification;
      status: NotificationStatus;
    }) => {
      return updateNotificationStatus(userInfo, notification, status);
    },
    onError: (error: AxiosError) => {
      console.error(error);
      emitEvent(EventConstants.ADD_ERROR, {
        message: error.message ?? "An error occurred",
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

const useRecommendationStatus = () => {
  return useMutation({
    mutationKey: [QueryKeys.UPDATE_RECOMMENDATION],
    mutationFn: ({
      userInfo,
      recommendation,
      status,
    }: {
      userInfo: IUserInfo;
      recommendation: Recommendation;
      status: NotificationStatus;
    }) => {
      return updateRecommendationStatus(userInfo, recommendation, status);
    },
    onError: (error: AxiosError) => {
      console.error(error);
      emitEvent(EventConstants.ADD_ERROR, {
        message: error.message ?? "An error occurred",
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};

export {
  useNotification,
  useRecommendation,
  useNotificationStatus,
  useRecommendationStatus,
};
